import { inject as service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { isPresent } from '@ember/utils';
import IntlService from 'ember-intl/services/intl';

interface IArgs {
  orientation?: 'vertical' | 'horizontal';
  onHand?: number;
  demand?: number;
  supply?: number;
  showNetNeed?: boolean;
}

export default class BvMrpViz extends Component<IArgs> {
  @service intl!: IntlService;

  get isVertical() {
    return (this.args.orientation ?? 'vertical') === 'vertical';
  }

  get dimension() {
    return this.isVertical ? 'height' : 'width';
  }

  get availableStock() {
    return (this.args.onHand ?? 0) + (this.args.supply ?? 0);
  }

  get demandAbs() {
    return Math.abs(this.args.demand ?? 0);
  }

  get total() {
    const demand = this.demandAbs;
    const availableStock = this.availableStock || 0;
    return demand > availableStock ? demand : availableStock;
  }

  // Tooltips

  get supplyTooltip() {
    return isPresent(this.args.supply)
      ? htmlSafe(`${this.intl.t('supply')} : ${this.intl.formatNumber(this.args.supply!)}`)
      : '';
  }

  get onHandTooltip() {
    return isPresent(this.args.onHand)
      ? htmlSafe(`${this.intl.t('onHand')} : ${this.intl.formatNumber(this.args.onHand!)}`)
      : '';
  }

  get demandTooltip() {
    return isPresent(this.demandAbs)
      ? htmlSafe(`${this.intl.t('demand')} : ${this.intl.formatNumber(this.args.demand!)}`)
      : '';
  }

  // Percentages
  //

  get onHandPct() {
    return this.computePct(this.total, this.args.onHand ?? 0);
  }

  get supplyPct() {
    return this.computePct(this.total, this.args.supply ?? 0);
  }

  get demandPct() {
    return this.computePct(this.total, this.demandAbs);
  }

  // Margins

  get supplyLeft() {
    return this.onHandPct;
  }

  get onHandLeft() {
    return 0;
  }

  // Styles

  get supplyStyle() {
    return this.computeStyle(
      this.args.orientation ?? 'vertical',
      this.dimension,
      this.supplyPct,
      this.supplyLeft
    );
  }

  get onHandStyle() {
    return this.computeStyle(
      this.args.orientation ?? 'vertical',
      this.dimension,
      this.onHandPct,
      this.onHandLeft
    );
  }

  get demandStyle() {
    let style = '';
    if (!this.isVertical) {
      if (this.demandPct <= 0.0) {
        style = 'left:-3px;';
      } else if (this.demandPct >= 100.0) {
        style = 'right:-3px;';
      } else {
        style = `left:${this.demandPct}%`;
      }
    } else {
      style = `bottom:${this.demandPct}%`;
    }

    return htmlSafe(style);
  }

  private computePct(total: number, value: number) {
    const pct = total === 0.0 ? 0.0 : value / total;
    return (pct < 0 ? 0 : pct) * 100.0;
  }

  private computeStyle(
    orientation: 'vertical' | 'horizontal',
    dimension: 'height' | 'width',
    pct: number,
    left: number
  ) {
    return htmlSafe(
      `${dimension}:${pct}%;` + (orientation === 'horizontal' ? `left:${left}%;` : '')
    );
  }
}
